/* eslint-disable @typescript-eslint/no-empty-function */
import { createContext, useContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import AuthenticationContext from '../../auth/AuthenticationContext';
import ScenarioBusinessPriority from '../../../../domain/enums/scenario/ScenarioBusinessPriority';
import ContactQualificationAiQualification from '../../../../domain/enums/contact/contact_qualification_ai/ContactQualificationAiQualification';
import updateScenario from '../../../../domain/use_cases/scenarios/updateScenario';
import deleteScenario, { DeleteScenarioFailure } from '../../../../domain/use_cases/scenarios/deleteScenario';
import Scenario from '../../../../domain/models/Scenario';
import { ScenarioSample } from '../../../../domain/models/scenario/ScenarioSample';
import { formatErrorMessage } from '../../../../core/utils/errorUtils';

interface ScenarioEditionFormContextProps {
  wipScenario: Scenario | null;
  updateStatus: ContextStatus;
  deleteStatus: ContextStatus;
  errorMessage: string;
  initWithScenario: (scenario: Scenario) => void;
  submitUpdate: () => Promise<void>;
  submitDelete: () => Promise<void>;
  onNameChange: (name: string) => void;
  onNameShortChange: (nameShort: string) => void;
  onBusinessPriorityChange: (priority: ScenarioBusinessPriority) => void;
  onDescriptionChange: (description: string) => void;
  onConstraintsChange: (constraints: string) => void;
  onCriteriaChange: (criteria: string[]) => void;
  onTargetChange: (target: string) => void;
  onBehaviorChange: (behavior: string) => void;
  onEscalationRulesChange: (
    escalationRules: ContactQualificationAiQualification[]
  ) => void;
  onEscalationNeedsQualificationChange: (escalationNeedsQualification: boolean) => void;
  onFidelityModelFirstMessageChange: (fidelityModelFirstMessage: boolean) => void;
  onQualificationInstructionsChange: (qualificationInstructions: string) => void;
  onSamplesChange: (samples: ScenarioSample[]) => void;
  onLinkCalendarBookingChange: (link_calendar_booking: string) => void;
}

const ScenarioEditionFormContext =
  createContext<ScenarioEditionFormContextProps>({
    wipScenario: null,
    updateStatus: ContextStatus.IDLE,
    deleteStatus: ContextStatus.IDLE,
    errorMessage: '',
    initWithScenario: (_: Scenario) => { },
    submitUpdate: async () => { },
    submitDelete: async () => { },
    onNameChange: (_: string) => { },
    onNameShortChange: (_: string) => { },
    onBusinessPriorityChange: (_: ScenarioBusinessPriority) => { },
    onDescriptionChange: (_: string) => { },
    onConstraintsChange: (_: string) => { },
    onCriteriaChange: (_: string[]) => { },
    onTargetChange: (_: string) => { },
    onBehaviorChange: (_: string) => { },
    onEscalationRulesChange: (_: ContactQualificationAiQualification[]) => { },
    onEscalationNeedsQualificationChange: (_: boolean) => { },
    onFidelityModelFirstMessageChange: (_: boolean) => { },
    onQualificationInstructionsChange: (_: string) => { },
    onSamplesChange: (_: ScenarioSample[]) => { },
    onLinkCalendarBookingChange: (_: string) => { }
  });

export const ScenarioEditionFormProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [wipScenario, setWipScenario] = useState<Scenario | null>(null);
  const [updateStatus, setUpdateStatus] = useState(ContextStatus.IDLE);
  const [deleteStatus, setDeleteStatus] = useState(ContextStatus.IDLE);
  const [errorMessage, setErrorMessage] = useState('');

  const authContext = useContext(AuthenticationContext);

  const initWithScenario = (scenario: Scenario) => {
    setWipScenario(scenario);
  };

  const submitUpdate = async () => {
    if (wipScenario === null || authContext.authentication === null) return;
    setUpdateStatus(ContextStatus.LOADING);
    try {
      await updateScenario(wipScenario, authContext.authentication);
      setUpdateStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setUpdateStatus(ContextStatus.ERROR);
    }
  };

  const submitDelete = async () => {
    if (wipScenario === null || authContext.authentication === null) return;
    setDeleteStatus(ContextStatus.LOADING);
    try {
      await deleteScenario(wipScenario, authContext.authentication);
      setDeleteStatus(ContextStatus.SUCCESS);
    } catch (e) {
      if (e instanceof DeleteScenarioFailure) {
        setErrorMessage(formatErrorMessage(e.message));
      }
      setDeleteStatus(ContextStatus.ERROR);
    }
  };

  const onNameChange = (name: string) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ name }));
  };

  const onNameShortChange = (nameShort: string) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ nameShort }));
  };

  const onBusinessPriorityChange = (priority: ScenarioBusinessPriority) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ businessPriority: priority }));
  };

  const onDescriptionChange = (description: string) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ description }));
  };

  const onConstraintsChange = (constraints: string) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ constraints }));
  };

  const onCriteriaChange = (criteria: string[]) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ criteria }));
  };

  const onTargetChange = (target: string) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ target }));
  };

  const onBehaviorChange = (behavior: string) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ behavior }));
  };

  const onEscalationRulesChange = (
    escalationRules: ContactQualificationAiQualification[]
  ) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ escalationRules }));
  };

  const onEscalationNeedsQualificationChange = (escalationNeedsQualification: boolean) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ escalationNeedsQualification }));
  };

  const onFidelityModelFirstMessageChange = (fidelityModelFirstMessage: boolean) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ fidelityModelFirstMessage }));
  };

  const onQualificationInstructionsChange = (qualificationInstructions: string) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ qualificationInstructions }));
  };

  const onSamplesChange = (samples: ScenarioSample[]) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ samples }));
  };

  const onLinkCalendarBookingChange = (link_calendar_booking: string) => {
    if (!wipScenario) return;
    setWipScenario(wipScenario.copyWith({ link_calendar_booking }));
  };

  return (
    <ScenarioEditionFormContext.Provider
      value={{
        wipScenario,
        updateStatus,
        deleteStatus,
        errorMessage,
        initWithScenario,
        submitUpdate,
        submitDelete,
        onNameChange,
        onNameShortChange,
        onBusinessPriorityChange,
        onDescriptionChange,
        onConstraintsChange,
        onCriteriaChange,
        onTargetChange,
        onBehaviorChange,
        onEscalationRulesChange,
        onEscalationNeedsQualificationChange,
        onFidelityModelFirstMessageChange,
        onQualificationInstructionsChange,
        onSamplesChange,
        onLinkCalendarBookingChange
      }}
    >
      {children}
    </ScenarioEditionFormContext.Provider>
  );
};

export default ScenarioEditionFormContext;
